// src/components/AdminPanel/Calendar.js
import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allInterviews, setAllInterviews] = useState([]);
  const [hoveredCandidate, setHoveredCandidate] = useState(null);

  // Fetch data (unchanged)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const interviewsSnap = await getDocs(collection(db, "interviews"));
        const interviewsData = interviewsSnap.docs
          .map((doc) => ({
            id: doc.id,
            source: "admin",
            name: doc.data().name,
            date: doc.data().date,
            time: doc.data().time,
            position: doc.data().position,
            status: doc.data().status || "pending",
            type: "interview",
            applicationType: "Interview",
          }))
          .filter((item) => item.date && item.time && 
            !["selected", "rejected"].includes(item.status?.toLowerCase()));

        const applicationsSnap = await getDocs(collection(db, "jobApplications"));
        const applicationsData = applicationsSnap.docs
          .map((doc) => ({
            id: doc.id,
            source: "candidate",
            name: doc.data().name,
            date: doc.data().interviewDate,
            time: doc.data().interviewSlot,
            position: doc.data().jobTitle,
            status: doc.data().status || "pending",
            type: "job",
            applicationType: "Job Application",
          }))
          .filter((item) => item.date && item.time && 
            !["selected", "rejected"].includes(item.status?.toLowerCase()));

        const reservationSnap = await getDocs(collection(db, "opportunityApplications"));
        const reservationData = reservationSnap.docs
          .map((doc) => ({
            id: doc.id,
            source: "reservation",
            name: doc.data().name,
            date: doc.data().interviewDate,
            time: doc.data().interviewSlot,
            position: doc.data().selectedField,
            opportunityType: doc.data().opportunityTitle,
            status: doc.data().status || "pending",
            type: "reservation",
            applicationType: "Reservation",
          }))
          .filter((item) => item.date && item.time && 
            !["selected", "rejected"].includes(item.status?.toLowerCase()));

        setAllInterviews([...interviewsData, ...applicationsData, ...reservationData]);
      } catch (error) {
        console.error("Error fetching calendar data:", error);
      }
    };

    fetchData();
  }, []);

  const getEventStyle = (event) => {
    if (event.type === "reservation") {
      return {
        card: "bg-emerald-100 border-emerald-400 hover:bg-emerald-200",   // Changed for Reservation
        dot: "bg-emerald-600",
        text: "text-emerald-800",
      };
    }
    if (event.type === "job") {
      return {
        card: "bg-orange-100 border-orange-400 hover:bg-orange-200",     // Changed for Job Application
        dot: "bg-orange-600",
        text: "text-orange-800",
      };
    }
    // Admin interview (default - Blue)
    return {
      card: "bg-blue-50 border-blue-200 hover:border-blue-400",
      dot: "bg-blue-500",
      text: "text-blue-700",
    };
  };

  const generateCalendar = () => {
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDay = firstDay.getDay();

    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Empty slots
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className="day empty"></div>);
    }

    // Calendar days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
      const dateStr = date.toLocaleDateString("en-CA");

      const events = allInterviews.filter((e) => {
        if (!e.date) return false;
        const [day, month, year] = e.date.split("-");
        return `${year}-${month}-${day}` === dateStr;
      });

      const isToday = date.toLocaleDateString("en-CA") === today.toLocaleDateString("en-CA");

      days.push(
        <div
          key={i}
          className={`day ${isToday ? "today" : ""} ${events.length ? "booked" : "available"}`}
        >
          <div className="date-num">{i}</div>

          {events.map((event, idx) => {
            const style = getEventStyle(event);
            return (
              <div
                key={idx}
                className={`event hoverable rounded-md border px-2 py-1.5 text-xs cursor-pointer transition-all ${style.card}`}
                onMouseEnter={(e) =>
                  setHoveredCandidate({
                    ...event,
                    x: e.clientX,
                    y: e.clientY,
                    accent: style.accent || "#3B82F6",   // fallback for tooltip
                  })
                }
                onMouseLeave={() => setHoveredCandidate(null)}
              >
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${style.dot}`} />
                  <span className={`font-medium truncate ${style.text}`}>
                    {event.name?.split(" ")[0] || "Unnamed"}
                  </span>
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5 pl-3.5">
                  {event.time}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return days;
  };

  const changeMonth = (dir) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + dir);
    setCurrentDate(newDate);
  };

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="calendar-page p-6">
      <h1 className="page-heading flex items-center gap-3 text-2xl font-semibold mb-6">
        <i className="fas fa-calendar-alt"></i> Interview Calendar
      </h1>

      <div className="calendar-header flex items-center justify-between mb-6">
        <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-gray-100 rounded">
          <i className="fas fa-chevron-left"></i>
        </button>
        <h3 className="text-xl font-semibold">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
        <button onClick={() => changeMonth(1)} className="p-2 hover:bg-gray-100 rounded">
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>

      <div className="days-of-week grid grid-cols-7 mb-2">
        {daysOfWeek.map((day) => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="calendar-grid grid grid-cols-7 gap-1">
        {generateCalendar()}
      </div>

      {/* Enhanced Tooltip - UNCHANGED as per your request */}
      {hoveredCandidate && (
        <div
          className="hover-tooltip fixed z-[1000] pointer-events-none"
          style={{
            top: hoveredCandidate.y - 20,
            left: hoveredCandidate.x + 20,
          }}
        >
          <div 
            className="bg-white rounded-xl shadow-xl border p-5 min-w-[260px] text-sm"
            style={{ borderColor: hoveredCandidate.accent + "40" }}
          >
            <div className="flex items-start gap-3">
              <div 
                className="w-3 h-3 mt-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: hoveredCandidate.accent }}
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-base mb-1">
                  {hoveredCandidate.name}
                </h4>
                
                <div className="space-y-2 text-gray-600">
                  <p><strong>Time:</strong> {hoveredCandidate.time}</p>
                  <p><strong>Position:</strong> {hoveredCandidate.position}</p>
                  {hoveredCandidate.opportunityType && (
                    <p><strong>Opportunity:</strong> {hoveredCandidate.opportunityType}</p>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t text-xs" 
                     style={{ borderColor: hoveredCandidate.accent + "30" }}>
                  <span 
                    className="inline-block px-3 py-1 rounded-full font-medium"
                    style={{ 
                      backgroundColor: hoveredCandidate.accent + "15",
                      color: hoveredCandidate.accent 
                    }}
                  >
                    {hoveredCandidate.applicationType}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;